import { Order, OrderItem, Payment } from '@artup/model'
import { APIClient } from './api-client'

/**
 * Payments and Orders API client
 */
export class PaymentsAPIClient extends APIClient {
  /**
   * Returns API name served by this client
   */
  get name () {
    return 'Payments'
  }


  /**
   * Returns summary statistics of orders and payments, for administrators
   * @returns {Promise<{ orders: Object, payments: Object }>} Counts of orders and payments per status
   */
  async getOrdersSummary () {
    try {
      const method = 'get'
      const url = this.endpoint('orders', 'summary')
      const { summary } = await this.request({ method, url })
      return summary
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Returns a list of orders with their basic data, most recent first, for administrators
   * @param {Object} params
   * @param {Number} params.limit Maximal number of orders to return
   * @returns {Promise<Object[]>} Order listing items
   */
  async getOrders ({ limit } = {}) {
    try {
      const method = 'get'
      const url = this.endpoint('orders')
      const params = limit ? { limit } : undefined
      const { orders } = await this.request({ method, url, params })
      return orders || []
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Returns a list of payments with their basic data, most recent first, for administrators
   * @param {Object} params
   * @param {Number} params.limit Maximal number of payments to return
   * @returns {Promise<Object[]>} Payment listing items
   */
  async getPayments ({ limit } = {}) {
    try {
      const method = 'get'
      const url = this.endpoint('payments')
      const params = limit ? { limit } : undefined
      const { payments } = await this.request({ method, url, params })
      return payments || []
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Retrieves an order
   * @param {Object} params
   * @param {String} params.id Order identifier
   * @returns {Promise<Order>} Retrieved order
   */
  async getOrder ({ id } = {}) {
    try {
      const method = 'get'
      const url = this.endpoint('order', id)
      const { order } = await this.request({ method, url })
      return this.asOrder(order)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Saves an order
   * @param {Object} params
   * @param {Order} params.order Order to save
   * @returns {Promise<Order>} Saved order
   */
  async saveOrder ({ order } = {}) {
    try {
      const data = order
      const method = 'post'
      const url = this.endpoint('order')
      const { order: savedOrder } = await this.request({ method, url, data })
      return this.asOrder(savedOrder)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Updates the status of an order
   * @param {Object} params
   * @param {Order} params.order Order to save
   * @returns {Promise<Order>} Saved order
   */
  async setOrderStatus ({ order } = {}) {
    try {
      const method = 'put'
      const url = this.endpoint('order', order.id, 'status')
      const data = {
        status: order.status
      }
      const { order: savedOrder } = await this.request({ method, url, data })
      return this.asOrder(savedOrder)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Deletes an order
   * @param {Object} params
   * @param {Order} params.order Order to delete
   * @returns {Promise<Order>} Deleted order
   */
  async deleteOrder ({ order } = {}) {
    try {
      const method = 'delete'
      const url = this.endpoint('order', order.id)
      const { order: deletedOrder } = await this.request({ method, url })
      return this.asOrder(deletedOrder)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Initializes payment for the specified order
   * @param {Object} params Input parameters
   * @param {Order} params.order Order to initialize payment for
   * @param {String} params.returnUrl URL to which the user will be redirected after the payment has been completed or cancelled
   * @returns {Promise<Payment>}
   */
  async initializePayment ({ order, returnUrl } = {}) {
    try {
      const method = 'post'
      const url = this.endpoint('order', order.id, 'payment')
      const data = { returnUrl }
      const { payment } = await this.request({ method, url, data })
      return this.asPayment(payment)

    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Updates the status of the specified payment
   * @param {Object} params Input parameters
   * @param {Order} params.order Order to update the payment status for
   * @param {PaymentStatus} params.status Payment status
   * @returns {Promise<Payment>}
   */
  async setPaymentStatus ({ order, status } = {}) {
    try {
      const method = 'put'
      const url = this.endpoint('order', order.id, 'payment')
      const data = { status }
      const { payment } = await this.request({ method, url, data })
      return this.asPayment(payment)

    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Retrieves the status of the specified payment
   * @param {Object} params Input parameters
   * @param {Order} params.order Order to check the payment status for
   * @returns {Promise<Payment>}
   */
  async getPaymentStatus ({ order } = {}) {
    try {
      const method = 'get'
      const url = this.endpoint('order', order.id, 'payment')
      const { payment } = await this.request({ method, url })
      return this.asPayment(payment)

    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Checks the status of the order and the payment with the payment provider
   * and refreshes in the database
   * @param {Object} params Input parameters
   * @param {Order} params.order Order to refresh the status for
   * @returns {Promise<Order>}
   */
  async refreshOrderStatus ({ order } = {}) {
    try {
      const method = 'put'
      const url = this.endpoint('order', order.id, 'refresh')
      const { order: updatedOrder } = await this.request({ method, url })
      return this.asOrder(updatedOrder)

    } catch (error) {
      this.handleError(error)
    }
  }

  asOrder (item) {
    if (item) {
      const order = new Order(item)
      return order
    }
  }

  asOrderItem (item) {
    if (item) {
      const orderItem = new OrderItem(item)
      return orderItem
    }
  }

  asPayment (item) {
    if (item) {
      const payment = new Payment(item)
      return payment
    }
  }
}
